import {
  CreateTprmExternalQuestionnaire,
  EngagementId,
  TprmExternalQuestionnaire,
  TprmExternalQuestionnaireId,
  TprmExternalQuestionnaireMin,
  TprmQuestionTemplateId,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function create(
  externalQuestionnaire: CreateTprmExternalQuestionnaire,
): Promise<TprmExternalQuestionnaire> {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/external_questionnaires", {
    body: externalQuestionnaire,
  });
  await invalidateQueries([
    q.tprm.engagementExternalQuestionnaires.filter(externalQuestionnaire.engagement_id),
    q.tprm.externalQuestionnaires.filter(),
  ]);
  return res;
}

export async function list(): Promise<TprmExternalQuestionnaireMin[]> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/external_questionnaires", {});
}

export async function get(externalQuestionnaireId: TprmExternalQuestionnaireId): Promise<TprmExternalQuestionnaire> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}",
    {path: {external_questionnaire_id: externalQuestionnaireId}},
  );
}

export async function delete_(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  engagementId: EngagementId,
): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}",
    {path: {external_questionnaire_id: externalQuestionnaireId}},
  );
  await invalidateQueries([
    q.tprm.externalQuestionnaire.filter(externalQuestionnaireId),
    q.tprm.engagementExternalQuestionnaires.filter(engagementId),
    q.tprm.externalQuestionnaires.filter(),
  ]);
}

export async function unlock(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  engagementId: EngagementId,
  options?: {notify_client?: boolean; email_context?: string},
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/unlock",
    {
      path: {external_questionnaire_id: externalQuestionnaireId},
      body: {notify_client: options?.notify_client ?? false, email_context: options?.email_context ?? null},
    },
  );
  await invalidateQueries([
    q.tprm.externalQuestionnaire.filter(externalQuestionnaireId),
    q.tprm.engagementExternalQuestionnaires.filter(engagementId),
    q.tprm.externalQuestionnaires.filter(),
  ]);
}

export async function lock(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  engagementId: EngagementId,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/lock",
    {path: {external_questionnaire_id: externalQuestionnaireId}},
  );
  await invalidateQueries([
    q.tprm.externalQuestionnaire.filter(externalQuestionnaireId),
    q.tprm.engagementExternalQuestionnaires.filter(engagementId),
    q.tprm.externalQuestionnaires.filter(),
  ]);
}

export async function setClientVisibility(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  engagementId: EngagementId,
  clientVisible: boolean,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/client_visibility",
    {path: {external_questionnaire_id: externalQuestionnaireId}, body: clientVisible},
  );
  await invalidateQueries([
    q.tprm.externalQuestionnaire.filter(externalQuestionnaireId),
    q.tprm.engagementExternalQuestionnaires.filter(engagementId),
    q.tprm.externalQuestionnaires.filter(),
  ]);
}

// Move a questionnaire out of the auditor-scoping phase and notify the client.
export async function release(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  engagementId: EngagementId,
  options?: {notify_client?: boolean; email_context?: string},
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/release",
    {
      path: {external_questionnaire_id: externalQuestionnaireId},
      body: {notify_client: options?.notify_client ?? false, email_context: options?.email_context ?? null},
    },
  );
  await invalidateQueries([
    q.tprm.externalQuestionnaire.filter(externalQuestionnaireId),
    q.tprm.engagementExternalQuestionnaires.filter(engagementId),
    q.tprm.externalQuestionnaires.filter(),
  ]);
}

export async function updateAuditorTextAnswer(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  text: string,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/auditor_answers/{question_template_id}/text",
    {path: {external_questionnaire_id: externalQuestionnaireId, question_template_id: questionTemplateId}, body: text},
  );
  await invalidateQueries([q.tprm.externalQuestionnaire.filter(externalQuestionnaireId)]);
}

export async function updateAuditorSelectAnswer(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  values: string[],
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/auditor_answers/{question_template_id}/select",
    {
      path: {external_questionnaire_id: externalQuestionnaireId, question_template_id: questionTemplateId},
      body: values,
    },
  );
  await invalidateQueries([q.tprm.externalQuestionnaire.filter(externalQuestionnaireId)]);
}

export async function updateAuditorNotApplicable(
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  questionTemplateId: TprmQuestionTemplateId,
  notApplicable: boolean,
): Promise<void> {
  await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/auditor_answers/{question_template_id}/not_applicable",
    {
      path: {external_questionnaire_id: externalQuestionnaireId, question_template_id: questionTemplateId},
      body: notApplicable,
    },
  );
  await invalidateQueries([q.tprm.externalQuestionnaire.filter(externalQuestionnaireId)]);
}
